import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getOrderById, getOrderReturns, createReturn, updateOrderKitchenStatus, printOrderReceipt } from "../services/api";
import { AuthContext } from "../context/AuthContext";
import { ICONS } from "../icons";
import { notify } from "../utils/notify";
import PageHeader from "../components/UI/PageHeader";
import Button from "../components/UI/Button";
import SelectBox from "../components/UI/SelectBox";
import ToggleSwitch from "../components/UI/ToggleSwitch";
import FloatingInput from "../components/UI/FloatingInput";
import { aggregateTaxBreakdown, parseItemBreakdown, taxRateLabel } from "../utils/taxBreakdown";
import { useTranslation } from "../context/I18nContext";

function money(n) {
  return Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const STATUS_BADGE = {
  completed: "bg-green-50 text-green-600",
  partially_refunded: "bg-amber-50 text-amber-600",
  refunded: "bg-slate-100 text-slate-500",
  held: "bg-blue-50 text-blue-600",
  cancelled: "bg-red-50 text-red-500",
};

// KITCHEN_STATUS_FLOW mirrors models.OrderKitchenStatus* in order.go — the
// order this array lists them in IS the forward progression a dine-in
// order's kitchen status moves through, one step at a time (see
// handleAdvanceKitchenStatus below).
const KITCHEN_STATUS_FLOW = ["pending", "preparing", "ready", "served"];
const KITCHEN_STATUS_BADGE = {
  pending: "bg-gray-100 text-gray-500",
  preparing: "bg-amber-50 text-amber-600",
  ready: "bg-blue-50 text-blue-600",
  served: "bg-green-50 text-green-600",
};

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const { t } = useTranslation("order_detail");
  const { t: tCommon } = useTranslation("common");
  const { userPermissions } = useContext(AuthContext);
  const canRefund = userPermissions?.includes("order_refund");
  const canUpdateKitchen = userPermissions?.includes("order_kitchen_update");
  const canPrint = userPermissions?.includes("print_receipt");
  const [order, setOrder] = useState(null);
  const [updatingKitchen, setUpdatingKitchen] = useState(false);
  const [printing, setPrinting] = useState(false);

  // Manual "Print Receipt" — queues a job on the branch's default printer
  // (or, if none is configured yet, surfaces the backend's own "No default
  // printer configured" error rather than silently doing nothing). See
  // PrintOrderReceiptHandler's own doc comment in order_print.go — this
  // never blocks on the print agent actually being online; the job just
  // sits durably queued until it is.
  const handlePrintReceipt = async () => {
    setPrinting(true);
    try {
      await printOrderReceipt(token, order.id);
      notify.success(t("notify_print_queued", "Receipt queued for printing"));
    } catch (err) {
      notify.error(err.data?.error || err.message || t("notify_print_failed", "Failed to queue the receipt for printing"));
    } finally {
      setPrinting(false);
    }
  };

  const KITCHEN_STATUS_LABELS = {
    pending: t("kitchen_status_pending", "Pending"),
    preparing: t("kitchen_status_preparing", "Preparing"),
    ready: t("kitchen_status_ready", "Ready"),
    served: t("kitchen_status_served", "Served"),
  };

  const REFUND_METHOD_OPTIONS = [
    { value: "cash", label: t("refund_method_cash", "Cash") },
    { value: "wallet", label: t("refund_method_wallet", "Wallet Credit") },
    { value: "voucher", label: t("refund_method_voucher", "Purchase Voucher") },
  ];

  const REFUND_METHOD_LABELS = {
    cash: t("refund_method_cash", "Cash"),
    wallet: t("refund_method_wallet", "Wallet Credit"),
    voucher: t("refund_method_voucher", "Purchase Voucher"),
  };

  // returns is this order's own return history (see return_handler.go) —
  // loaded alongside the order and refreshed after processing a new one.
  // Reused both to render "Return History" below and to compute how much
  // of each line is still left to return (see remainingQtyByItem).
  const [returns, setReturns] = useState([]);

  const loadOrder = () => {
    getOrderById(token, id).then(setOrder).catch(() => notify.error(t("notify_load_failed", "Failed to load order")));
  };
  const loadReturns = () => {
    getOrderReturns(token, id).then((rows) => setReturns(rows || [])).catch(() => {});
  };
  useEffect(() => { loadOrder(); loadReturns(); }, [id, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Moves this dine-in order one step forward through KITCHEN_STATUS_FLOW
  // — see UpdateKitchenStatusHandler in order_handler.go, which is the
  // actual authority on whether this is allowed (the button below already
  // only renders once canUpdateKitchen/order_type/order.status all check
  // out, this just surfaces whatever it says no to).
  const handleAdvanceKitchenStatus = async () => {
    const currentIdx = KITCHEN_STATUS_FLOW.indexOf(order.kitchen_status);
    const next = KITCHEN_STATUS_FLOW[currentIdx + 1];
    if (!next) return;
    setUpdatingKitchen(true);
    try {
      await updateOrderKitchenStatus(token, order.id, next);
      notify.success(t("notify_kitchen_status_updated", "Kitchen status updated to {status}").replace("{status}", KITCHEN_STATUS_LABELS[next] || next));
      loadOrder();
    } catch (err) {
      notify.error(err.data?.error || err.message || t("notify_kitchen_status_failed", "Failed to update kitchen status"));
    } finally {
      setUpdatingKitchen(false);
    }
  };

  // Hooks must run on every render regardless of whether `order` has
  // loaded yet, so these stay above the early return below and just guard
  // internally with `order?.items || []`.
  const itemBreakdowns = useMemo(() => (order?.items || []).map(parseItemBreakdown), [order]);
  const taxBreakdown = useMemo(() => aggregateTaxBreakdown(itemBreakdowns), [itemBreakdowns]);

  // remainingQtyByItem maps an OrderItem id to how much of it hasn't been
  // returned yet, across EVERY Return this order has ever had — mirrors
  // CreateReturnHandler's own "alreadyReturned" calculation exactly, so
  // the modal below never lets the cashier try to over-return a line the
  // backend would reject anyway.
  const remainingQtyByItem = useMemo(() => {
    const returnedByItem = {};
    returns.forEach((r) => (r.items || []).forEach((ri) => {
      returnedByItem[ri.order_item_id] = (returnedByItem[ri.order_item_id] || 0) + ri.quantity;
    }));
    const map = {};
    (order?.items || []).forEach((it) => { map[it.id] = it.quantity - (returnedByItem[it.id] || 0); });
    return map;
  }, [order, returns]);

  // --- Process Return modal ---
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returnQty, setReturnQty] = useState({}); // {orderItemId: "quantity string"}
  const [returnRestock, setReturnRestock] = useState({}); // {orderItemId: bool}
  const [refundMethod, setRefundMethod] = useState("cash");
  const [returnReason, setReturnReason] = useState("");
  const [submittingReturn, setSubmittingReturn] = useState(false);

  const openReturnModal = () => {
    const qty = {};
    const restock = {};
    (order.items || []).forEach((it) => { qty[it.id] = ""; restock[it.id] = true; });
    setReturnQty(qty);
    setReturnRestock(restock);
    setRefundMethod("cash");
    setReturnReason("");
    setShowReturnModal(true);
  };

  const handleSubmitReturn = async () => {
    const items = (order.items || [])
      .map((it) => ({ order_item_id: it.id, quantity: Number(returnQty[it.id] || 0), restock: !!returnRestock[it.id] }))
      .filter((it) => it.quantity > 0);
    if (items.length === 0) {
      notify.error(t("validation_return_qty_required", "Enter a quantity to return for at least one item"));
      return;
    }
    setSubmittingReturn(true);
    try {
      const created = await createReturn(token, id, { refund_method: refundMethod, reason: returnReason.trim(), items });
      const currencyLabel = order.currency?.symbol || order.currency?.code || "";
      if (created.issued_voucher) {
        // The code is wrapped in Unicode LRI/PDI isolate marks (U+2066 /
        // U+2069) — this is a plain toast string, not JSX, so there's no
        // <bdi> to reach for. Without isolating it, a code with no strong
        // leading Latin letters (or a hyphen sitting right at the RTL/LTR
        // boundary) can visually reorder inside an Arabic sentence per the
        // Unicode Bidi Algorithm — reads as if the prefix vanished even
        // though the underlying string is correct.
        notify.success(t("notify_return_processed_voucher", "Return processed — voucher {code} issued for {amount}").replace("{code}", `⁦${created.issued_voucher.code}⁩`).replace("{amount}", `${money(created.refund_amount)} ${currencyLabel}`));
      } else {
        notify.success(t("notify_return_processed_refund", "Return processed — {amount} refunded").replace("{amount}", `${money(created.refund_amount)} ${currencyLabel}`));
      }
      setShowReturnModal(false);
      loadOrder();
      loadReturns();
    } catch (err) {
      notify.error(err.data?.error || err.message || t("notify_return_failed", "Failed to process return"));
    } finally {
      setSubmittingReturn(false);
    }
  };

  if (!order) {
    return <div className="p-8 text-center text-gray-400">{t("loading_label", "Loading…")}</div>;
  }

  const currencyLabel = order.currency?.symbol || order.currency?.code || "";

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto">
      <PageHeader className="flex items-center gap-4 mb-8 border-b border-gray-100 pb-6">
        <button onClick={() => navigate("/orders")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
          <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
        </button>
        <div>
          <h2 className="text-2xl font-black text-gray-800 tracking-tight">{t("title_order_number", "Order #{id}").replace("{id}", order.id)}</h2>
          <p className="text-gray-400 text-sm italic font-medium">{new Date(order.created_at).toLocaleString()}</p>
          {/* Only set when the order actually completed (see
              services.NextInvoiceNumber) — a held/cancelled order, or one
              placed by a tax-exempt customer, never gets one. */}
          {order.invoice_number && (
            <p className="text-xs font-mono font-bold text-[var(--primary-color)] mt-0.5">{t("label_invoice", "Invoice {number}").replace("{number}", order.invoice_number)}</p>
          )}
        </div>
        <div className="ml-auto flex items-center gap-3">
          {canPrint && (
            <Button variant="outline" size="sm" icon={ICONS.Print} onClick={handlePrintReceipt} loading={printing}>
              {t("btn_print_receipt", "Print Receipt")}
            </Button>
          )}
          {canRefund && (order.status === "completed" || order.status === "partially_refunded") && (
            <Button variant="outline" size="sm" icon={ICONS.Returns} onClick={openReturnModal}>
              {t("btn_return_items", "Return Items")}
            </Button>
          )}
          <span className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase ${STATUS_BADGE[order.status] || "bg-gray-50 text-gray-500"}`}>
            {order.status?.replace("_", " ")}
          </span>
        </div>
      </PageHeader>

      <div className="bg-white p-6 md:p-8 rounded-[2.5rem] border border-gray-100 shadow-sm space-y-8">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          <div>
            <div className="text-gray-400 text-xs uppercase font-bold tracking-wide mb-1">{t("label_branch", "Branch")}</div>
            <div className="font-bold text-gray-800">{order.branch?.name || `#${order.branch_id}`}</div>
          </div>
          <div>
            <div className="text-gray-400 text-xs uppercase font-bold tracking-wide mb-1">{t("label_cashier", "Cashier")}</div>
            <div className="font-bold text-gray-800">{order.cashier?.username || `#${order.cashier_id}`}</div>
          </div>
          <div>
            <div className="text-gray-400 text-xs uppercase font-bold tracking-wide mb-1">{t("label_customer", "Customer")}</div>
            <div className="font-bold text-gray-800">
              {order.customer?.name || t("customer_walkin", "Walk-in")}
              {order.customer?.is_tax_exempt && (
                <span className="ms-2 align-middle text-[10px] font-bold text-emerald-600 uppercase">{t("badge_tax_exempt", "Tax Exempt")}</span>
              )}
            </div>
          </div>
          <div>
            <div className="text-gray-400 text-xs uppercase font-bold tracking-wide mb-1">{t("label_order_type", "Order Type")}</div>
            <div className="font-bold text-gray-800 capitalize">{order.order_type?.replace("_", " ")}</div>
          </div>
          {/* Only set on a dine-in order with a Table actually chosen — see
              Order.TableID in models/order.go. */}
          {order.table && (
            <div>
              <div className="text-gray-400 text-xs uppercase font-bold tracking-wide mb-1">{t("label_table", "Table")}</div>
              <div className="font-bold text-gray-800">{order.table.name}{order.table.section && ` (${order.table.section.name})`}</div>
            </div>
          )}
        </div>

        {/* Kitchen status — only ever meaningful for a dine-in order (see
            Order.KitchenStatus's own doc comment in order.go); every other
            order type never had one set at all, so this section doesn't
            render for them. */}
        {order.order_type === "dine_in" && (
          <div className="flex items-center justify-between gap-4 bg-gray-50/60 rounded-2xl p-4">
            <div>
              <div className="text-gray-400 text-xs uppercase font-bold tracking-wide mb-1">{t("label_kitchen_status", "Kitchen Status")}</div>
              <span className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase ${KITCHEN_STATUS_BADGE[order.kitchen_status] || "bg-gray-100 text-gray-500"}`}>
                {KITCHEN_STATUS_LABELS[order.kitchen_status] || order.kitchen_status}
              </span>
            </div>
            {canUpdateKitchen && order.status !== "cancelled" && KITCHEN_STATUS_FLOW.indexOf(order.kitchen_status) < KITCHEN_STATUS_FLOW.length - 1 && (
              <Button variant="primary" size="sm" onClick={handleAdvanceKitchenStatus} loading={updatingKitchen}>
                {t("btn_advance_kitchen_status", "Mark as {status}").replace("{status}", KITCHEN_STATUS_LABELS[KITCHEN_STATUS_FLOW[KITCHEN_STATUS_FLOW.indexOf(order.kitchen_status) + 1]])}
              </Button>
            )}
          </div>
        )}

        <div>
          <h3 className="text-xs uppercase font-bold text-gray-400 tracking-wide mb-3">{t("section_items", "Items")}</h3>
          <div className="overflow-x-auto rounded-2xl border border-gray-100">
            <table className="w-full text-sm">
              <thead>
                {/* text-start/text-end بدل text-left/text-right عشان محاذاة
                    الأعمدة تتقلب تلقائيًا مع اتجاه اللغة */}
                <tr className="bg-gray-50 text-gray-500 text-xs uppercase">
                  <th className="p-3 text-start">{t("col_product", "Product")}</th>
                  <th className="p-3 text-end">{t("col_qty", "Qty")}</th>
                  <th className="p-3 text-end">{t("col_unit_price", "Unit Price")}</th>
                  <th className="p-3 text-end">{t("col_discount", "Discount")}</th>
                  <th className="p-3 text-end">{t("col_tax", "Tax")}</th>
                  <th className="p-3 text-end">{t("col_line_total", "Line Total")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(order.items || []).map((item, idx) => {
                  const breakdown = itemBreakdowns[idx] || [];
                  return (
                    <tr key={item.id}>
                      <td className="p-3">
                        <div className="font-bold text-gray-800">{item.product_name}</div>
                        <div className="text-[10px] text-gray-400 font-mono">{item.product_sku}</div>
                        {item.notes && <div className="text-[11px] text-gray-400 italic">{item.notes}</div>}
                      </td>
                      <td className="p-3 text-end font-mono">
                        {/* unit_name is only set on orders created after multi-unit/fractional
                            support was added — older rows fall back to the plain base quantity. */}
                        {item.unit_name ? `${item.display_quantity} ${item.unit_name}` : item.quantity}
                      </td>
                      <td className="p-3 text-end font-mono">{money(item.unit_price)}</td>
                      <td className="p-3 text-end font-mono text-red-500">{item.discount_amount > 0 ? `-${money(item.discount_amount)}` : "—"}</td>
                      <td className="p-3 text-end">
                        <div className="font-mono">{money(item.tax_amount)}</div>
                        {breakdown.length > 0 && (
                          <div className="mt-1 space-y-0.5">
                            {breakdown.map((taxItem, i) => (
                              <div key={i} className="text-[10px] text-gray-400 whitespace-nowrap">
                                {taxItem.tax_name} ({taxRateLabel(taxItem, currencyLabel)}){taxItem.is_price_inclusive ? " · incl." : ""}: {money(taxItem.amount)}
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="p-3 text-end font-mono font-bold">{money(item.line_total)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
          <div>
            <h3 className="text-xs uppercase font-bold text-gray-400 tracking-wide mb-3">{t("section_payments", "Payments")}</h3>
            <div className="space-y-2">
              {(order.payments || []).map((p) => (
                <div key={p.id} className="bg-gray-50/60 rounded-xl px-4 py-2 text-sm">
                  <div className="flex justify-between items-center">
                    <span className="capitalize font-medium text-gray-700">{p.method}</span>
                    <span className="font-mono font-bold text-gray-800">{money(p.amount)} {currencyLabel}</span>
                  </div>
                  {/* Only shown when the cashier registered cash tendered
                      above the amount owed — see ChangeAmount in
                      CreateOrderHandler (order_handler.go) and the
                      quick-cash pad in Checkout.js. */}
                  {p.method === "cash" && p.change_amount > 0 && (
                    <div className="flex justify-between items-center mt-1 pt-1 border-t border-gray-100 text-[11px] text-gray-500">
                      <span>{t("label_received", "Received {amount}").replace("{amount}", `${money(p.received_amount)} ${currencyLabel}`)}</span>
                      <span className="font-mono font-bold text-emerald-600">{t("label_change", "Change {amount}").replace("{amount}", `${money(p.change_amount)} ${currencyLabel}`)}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-2 text-sm">
            <div className="flex justify-between text-gray-500">
              <span>{t("label_subtotal", "Subtotal")}</span>
              <span className="font-mono">{money(order.subtotal)}</span>
            </div>
            <div className="flex justify-between text-gray-500">
              <span>{t("col_discount", "Discount")}</span>
              <span className="font-mono text-red-500">{order.discount_total > 0 ? `-${money(order.discount_total)}` : "—"}</span>
            </div>
            <div>
              <div className="flex justify-between text-gray-500">
                <span>{t("col_tax", "Tax")}</span>
                <span className="font-mono">{money(order.tax_total)}</span>
              </div>
              {taxBreakdown.length > 0 && (
                <div className="mt-1 space-y-0.5 ps-3">
                  {taxBreakdown.map((taxItem, i) => (
                    <div key={i} className="flex justify-between text-[11px] text-gray-400">
                      <span>
                        {taxItem.tax_name} ({taxRateLabel(taxItem, currencyLabel)}){taxItem.is_price_inclusive ? " · incl." : ""}
                      </span>
                      <span className="font-mono">{money(taxItem.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex justify-between text-lg font-black text-gray-900 pt-2 border-t border-gray-100">
              <span>{t("label_grand_total", "Grand Total")}</span>
              <span className="font-mono">{money(order.grand_total)} {currencyLabel}</span>
            </div>
            {returns.length > 0 && (
              <div className="flex justify-between text-red-500 font-bold">
                <span>{t("label_refunded", "Refunded")}</span>
                <span className="font-mono">-{money(returns.reduce((sum, r) => sum + r.refund_amount, 0))} {currencyLabel}</span>
              </div>
            )}
          </div>
        </div>

        {returns.length > 0 && (
          <div>
            <h3 className="text-xs uppercase font-bold text-gray-400 tracking-wide mb-3">{t("section_return_history", "Return History")}</h3>
            <div className="space-y-3">
              {returns.map((r) => (
                <div key={r.id} className="bg-gray-50/60 rounded-2xl p-4 text-sm">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-gray-800">{t("label_return_number", "Return #{id}").replace("{id}", r.id)}</span>
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-white border border-gray-100 text-gray-500">
                        {REFUND_METHOD_LABELS[r.refund_method] || r.refund_method}
                      </span>
                      <span className="text-[11px] text-gray-400">{t("label_by", "by {name}").replace("{name}", r.processed_by?.username || `#${r.processed_by_id}`)}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-[11px] text-gray-400 italic">{new Date(r.created_at).toLocaleString()}</span>
                      <span className="font-mono font-bold text-red-500">-{money(r.refund_amount)} {currencyLabel}</span>
                    </div>
                  </div>
                  {r.issued_voucher && (
                    <p className="text-[11px] font-bold text-emerald-600 mb-2">
                      {/* The code is wrapped in Unicode LRI/PDI isolate marks
                          (U+2066/U+2069) — same reasoning as the post-return
                          toast above: this whole line is one plain string
                          (a translated template with the code spliced in),
                          not separate JSX nodes, so isolating just the code
                          run here keeps it from visually reordering inside
                          the surrounding Arabic text. */}
                      {t("label_voucher_issued", "Voucher {code} — balance {balance}").replace("{code}", `⁦${r.issued_voucher.code}⁩`).replace("{balance}", `${money(r.issued_voucher.balance)} ${currencyLabel}`)}
                      {r.issued_voucher.expires_at && (
                        <span className={new Date(r.issued_voucher.expires_at) < new Date() ? "text-red-500" : "text-gray-400 font-normal"}>
                          {" — "}
                          {t("label_voucher_expires", "Expires {date}").replace("{date}", new Date(r.issued_voucher.expires_at).toLocaleDateString())}
                        </span>
                      )}
                    </p>
                  )}
                  {r.reason && <p className="text-[11px] text-gray-400 italic mb-2">"{r.reason}"</p>}
                  <div className="space-y-1">
                    {(r.items || []).map((ri) => (
                      <div key={ri.id} className="flex justify-between text-xs text-gray-600">
                        <span>
                          {ri.order_item?.product_name || t("item_fallback_label", "Item #{id}").replace("{id}", ri.order_item_id)} × {ri.quantity}
                          <span className={`ms-2 text-[10px] font-bold uppercase ${ri.restock ? "text-emerald-600" : "text-gray-400"}`}>
                            {ri.restock ? t("label_restocked", "Restocked") : t("label_discarded", "Discarded")}
                          </span>
                        </span>
                        <span className="font-mono">{money(ri.refund_amount)} {currencyLabel}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {showReturnModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white rounded-[2rem] p-6 max-w-lg w-full shadow-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-gray-800 mb-4">{t("btn_return_items", "Return Items")}</h3>

            <div className="space-y-4">
              {(order.items || []).map((item) => {
                const remaining = remainingQtyByItem[item.id] ?? 0;
                if (remaining <= 0.0001) return null;
                const tracked = item.product?.track_inventory;
                return (
                  <div key={item.id} className="bg-gray-50/60 rounded-2xl p-3">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div>
                        <div className="font-bold text-sm text-gray-800">{item.product_name}</div>
                        <div className="text-[11px] text-gray-400">{t("label_left_to_return", "{remaining} left to return (of {total})").replace("{remaining}", remaining).replace("{total}", item.quantity)}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <input
                        type="number"
                        min="0"
                        max={remaining}
                        step="any"
                        value={returnQty[item.id] ?? ""}
                        onChange={(e) => setReturnQty((prev) => ({ ...prev, [item.id]: e.target.value }))}
                        placeholder="0"
                        className="w-24 rounded-xl border-2 border-gray-100 bg-white px-3 py-2 text-sm focus:outline-none focus:border-[var(--primary-color)]"
                      />
                      {tracked && (
                        <ToggleSwitch
                          label={t("toggle_restock_label", "Restock")}
                          checked={!!returnRestock[item.id]}
                          onChange={(val) => setReturnRestock((prev) => ({ ...prev, [item.id]: val }))}
                        />
                      )}
                      {!tracked && <span className="text-[11px] text-gray-400 italic">{t("label_not_stock_tracked", "Not stock-tracked")}</span>}
                    </div>
                  </div>
                );
              })}

              <SelectBox
                label={t("field_refund_method_label", "Refund Method")}
                // Wallet credit needs a customer to credit — a voucher
                // doesn't (it can be issued anonymously, redeemable by
                // whoever ends up with the code), so it stays available
                // either way.
                options={order.customer ? REFUND_METHOD_OPTIONS : REFUND_METHOD_OPTIONS.filter((o) => o.value !== "wallet")}
                value={refundMethod}
                onChange={setRefundMethod}
              />
              {!order.customer && (
                <p className="text-[11px] text-gray-400 -mt-2 ms-1">{t("hint_no_customer_wallet", "This order has no customer, so wallet credit isn't available — choose cash or a voucher.")}</p>
              )}

              <FloatingInput label={t("field_reason_label", "Reason (optional)")} value={returnReason} onChange={(e) => setReturnReason(e.target.value)} />
            </div>

            <div className="flex gap-3 mt-6">
              <Button variant="secondary" onClick={() => setShowReturnModal(false)} className="flex-1" disabled={submittingReturn}>
                {tCommon("btn_cancel", "Cancel")}
              </Button>
              <Button variant="primary" onClick={handleSubmitReturn} loading={submittingReturn} className="flex-1">
                {t("btn_process_return", "Process Return")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
